import { z } from 'zod';
/**
 * 0.18.0: tickets, the customer help desk and Trace's staff queue. A ticket is addressed by its number string
 * (for example NWF-142 inside an organization, or T-10421 across Trace), never by uuid.
 */
export const TICKET_MESSAGE_KINDS = ['reply', 'customer_note', 'trace_note', 'system'];
export const TICKET_TRACE_STATUSES = [
    'triage',
    'todo',
    'in_progress',
    'waiting_on_customer',
    'fix_in_progress',
    'done',
    'cancelled',
    'duplicate',
];
export const TICKET_CUSTOMER_STATUSES = [
    'open',
    'with_help_desk',
    'with_trace',
    'waiting_on_you',
    'resolved',
];
/**
 * Where Trace staff who manage tickets may move a closed ticket (done, cancelled, duplicate) to reopen it themselves;
 * any other open status from a closed one is refused (409 closed), and Triage is only ever entered by an escalation.
 */
export const TICKET_STAFF_REOPEN_TARGETS = [
    'todo',
    'in_progress',
];
export const TICKET_PRIORITIES = ['urgent', 'high', 'medium', 'low', 'none'];
export const TICKET_URGENCIES = ['blocking', 'soon', 'whenever'];
export const TICKET_ROUTINGS = ['help_desk', 'trace'];
export const TICKET_LINK_KINDS = ['linear', 'related', 'duplicate_of'];
export const ticketMessageKindSchema = z.enum(TICKET_MESSAGE_KINDS);
export const ticketTraceStatusSchema = z.enum(TICKET_TRACE_STATUSES);
export const ticketCustomerStatusSchema = z.enum(TICKET_CUSTOMER_STATUSES);
export const ticketPrioritySchema = z.enum(TICKET_PRIORITIES);
export const ticketUrgencySchema = z.enum(TICKET_URGENCIES);
export const ticketRoutingSchema = z.enum(TICKET_ROUTINGS);
export const ticketLinkKindSchema = z.enum(TICKET_LINK_KINDS);
/**
 * What the customer sees, derived and never stored: the Trace status wins while the ticket is with Trace, then the
 * help desk's own resolution, then who holds it.
 */
export function customerStatusOf(t) {
    if (t.escalated && t.traceStatus) {
        if (t.traceStatus === 'waiting_on_customer')
            return 'waiting_on_you';
        if (t.traceStatus === 'done' || t.traceStatus === 'cancelled' || t.traceStatus === 'duplicate')
            return 'resolved';
        return 'with_trace';
    }
    if (t.resolvedAt)
        return 'resolved';
    return t.handedBack ? 'with_help_desk' : 'open';
}
const bodySchema = z.string().trim().min(1).max(10000);
/**
 * Ticket attachments: what an upload may be, how large, and how many go on one message. The UIs check these before
 * uploading; platform-api enforces them (400 attachment_too_large at presign, 400 too_many_attachments on a message,
 * 422 attachment_type_mismatch when the uploaded bytes are not the declared type). SVG and HTML are never accepted:
 * both can carry script.
 */
export const TICKET_ATTACHMENT_MIME_TYPES = [
    'image/png',
    'image/jpeg',
    'image/webp',
    'image/gif',
    'application/pdf',
    'text/csv',
];
export const TICKET_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
export const TICKET_ATTACHMENTS_PER_MESSAGE = 10;
/** file: added by the person; screenshot: the page capture of the Get help popover. */
export const TICKET_ATTACHMENT_KINDS = ['file', 'screenshot'];
/**
 * The attachments of a new message. The bound here only limits the request size: the per-message limit
 * (TICKET_ATTACHMENTS_PER_MESSAGE) is the service's, so a client learns it from the reason too_many_attachments.
 */
const attachmentIdsSchema = z.array(z.uuid()).max(100).default([]);
/**
 * The page and browser state captured with a ticket. The url is a route template without query string or fragment
 * (the customer's data never rides in it); requests and errors are the most recent ones, bounded.
 */
export const ticketContextSchema = z.object({
    app: z.string().min(1).max(100),
    url: z
        .string()
        .max(500)
        .regex(/^\/[^?#]*$/),
    browser: z.string().max(300),
    viewport: z.object({
        width: z.number().int().min(0).max(20000),
        height: z.number().int().min(0).max(20000),
    }),
    requests: z
        .array(z.object({
        method: z.string().max(10),
        route: z.string().max(300),
        status: z.number().int(),
        durationMs: z.number().int(),
        requestId: z.string().max(64),
    }))
        .max(20)
        .default([]),
    errors: z
        .array(z.object({ message: z.string().max(500), frame: z.string().max(300) }))
        .max(10)
        .default([]),
    moduleStates: z.record(z.string(), z.unknown()).default({}),
});
export const ticketCreateRequestSchema = z.object({
    title: z.string().trim().min(3).max(140),
    body: bodySchema,
    urgency: ticketUrgencySchema.optional(),
    product: z.string().min(1).max(100).optional(),
    onBehalfOfUserId: z.uuid().optional(),
    routeToTrace: z.boolean().optional(),
    context: ticketContextSchema.optional(),
    attachmentIds: attachmentIdsSchema,
});
export const ticketReplyRequestSchema = z.object({
    kind: z.enum(['reply', 'customer_note']),
    body: bodySchema,
    attachmentIds: attachmentIdsSchema,
});
export const staffTicketReplyRequestSchema = z.object({
    kind: z.enum(['reply', 'trace_note']),
    body: bodySchema,
    attachmentIds: attachmentIdsSchema,
    setStatus: ticketTraceStatusSchema.optional(),
});
export const ticketAttachmentSchema = z.object({
    id: z.uuid(),
    fileName: z.string(),
    contentType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
});
export const ticketPersonSchema = z.object({ id: z.uuid().nullable(), name: z.string().nullable() });
export const ticketMessageSchema = z.object({
    id: z.uuid(),
    kind: ticketMessageKindSchema,
    body: z.string(),
    author: ticketPersonSchema,
    createdAt: z.iso.datetime(),
    attachments: z.array(ticketAttachmentSchema).default([]),
});
const ticketCommonShape = {
    id: z.uuid(),
    number: z.string(),
    organizationId: z.uuid(),
    title: z.string(),
    urgency: ticketUrgencySchema.nullable(),
    product: z.string().nullable(),
    customerStatus: ticketCustomerStatusSchema,
    requester: ticketPersonSchema,
    assignee: ticketPersonSchema.nullable(),
    routing: ticketRoutingSchema,
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    resolvedAt: z.iso.datetime().nullable(),
    messages: z.array(ticketMessageSchema).default([]),
    /**
     * The help desk's latest note for Trace, written when it escalated the ticket. Only the help desk (whoever reads all
     * the organization's tickets) and Trace staff receive it; for everyone else it is null.
     */
    escalationNote: z.string().nullable().default(null),
};
/** What the customer's people see: no Trace status, priority, labels, links or Trace notes. */
export const ticketCustomerViewSchema = z.object({
    ...ticketCommonShape,
    context: ticketContextSchema.nullable().default(null),
});
export const ticketLabelSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    color: z.string().nullable(),
    group: z.string().nullable(),
});
export const ticketLinkSchema = z.object({
    id: z.uuid(),
    kind: ticketLinkKindSchema,
    ref: z.string(),
    url: z.string().nullable(),
});
/**
 * One row of a ticket's timeline (ticket_events): who changed which field, from what to what, and when. before and
 * after are the recorded values (a status, a priority, label names, { kind, ref } of a link), except the assignee
 * fields (staff_assignee, customer_assignee), which are people ({ id, name } or null). A link to a ticket the viewer
 * may not open shows ref null.
 */
export const ticketEventSchema = z.object({
    id: z.uuid(),
    actor: z.object({
        id: z.uuid().nullable(),
        name: z.string().nullable(),
        kind: z.enum(['user', 'staff', 'system']),
    }),
    field: z.string(),
    before: z.unknown(),
    after: z.unknown(),
    at: z.iso.datetime(),
});
/** A person following a ticket (ticket_subscribers). */
export const ticketSubscriberSchema = z.object({ id: z.uuid(), displayName: z.string().nullable() });
/** Everything: Trace staff see the Trace status, priority, labels, links and the Trace notes. */
export const ticketStaffViewSchema = z.object({
    ...ticketCommonShape,
    context: ticketContextSchema.nullable().default(null),
    traceStatus: ticketTraceStatusSchema.nullable(),
    priority: ticketPrioritySchema,
    escalated: z.boolean(),
    handedBack: z.boolean(),
    labels: z.array(ticketLabelSchema).default([]),
    links: z.array(ticketLinkSchema).default([]),
    traceAssignee: ticketPersonSchema.nullable().default(null),
    // 0.18.0 (additive, same branch): the fields Trace Admin's ticket page shows. Defaults keep an older answer parsing.
    /** The customer's display name. */
    organizationName: z.string().default(''),
    escalatedAt: z.iso.datetime().nullable().default(null),
    /** When the first Trace reply is due (null: the priority has no target). */
    responseDueAt: z.iso.datetime().nullable().default(null),
    firstTraceReplyAt: z.iso.datetime().nullable().default(null),
    /** No Trace reply yet and the response target has passed. */
    overdue: z.boolean().default(false),
    /** Whether the caller follows the ticket. */
    subscribed: z.boolean().default(false),
    subscribers: z.array(ticketSubscriberSchema).default([]),
    /** The field-change timeline, oldest first (every row; staff see what the customer does not). */
    events: z.array(ticketEventSchema).default([]),
});
/** One row of a ticket list (the hub's queue and My tickets). Preview and unread cover only the kinds the viewer reads. */
export const ticketListItemSchema = z.object({
    id: z.uuid(),
    number: z.string(),
    organizationId: z.uuid(),
    title: z.string(),
    urgency: ticketUrgencySchema.nullable(),
    product: z.string().nullable(),
    customerStatus: ticketCustomerStatusSchema,
    requester: ticketPersonSchema,
    assignee: ticketPersonSchema.nullable(),
    createdAt: z.iso.datetime(),
    lastActivityAt: z.iso.datetime(),
    /** The start of the newest message the viewer may read (at most 140 characters), or null. */
    lastMessagePreview: z.string().nullable(),
    /** Messages the viewer may read, written by someone else since the viewer last opened the ticket. */
    unread: z.number().int().nonnegative(),
});
/**
 * 0.18.0 (additive): the hub list's counts over every ticket of the organization the caller may see, whatever the
 * filters. open: with the help desk (Open or With your help desk); unassigned: of those, with no assignee; withTrace:
 * escalated and not closed (With Trace or Waiting on you); resolvedThisMonth: resolved since the start of the current
 * UTC month; unread: tickets with a message the caller has not read; attention: unassigned or unread (the Help desk
 * badge).
 */
export const ticketListCountsSchema = z.object({
    open: z.number().int().nonnegative(),
    unassigned: z.number().int().nonnegative(),
    withTrace: z.number().int().nonnegative(),
    resolvedThisMonth: z.number().int().nonnegative(),
    unread: z.number().int().nonnegative(),
    attention: z.number().int().nonnegative(),
});
export const ticketListSchema = z.object({
    items: z.array(ticketListItemSchema),
    /** Opaque keyset cursor of the next page, or null on the last page. */
    next: z.string().nullable(),
    counts: ticketListCountsSchema,
});
/** GET /v1/me/tickets: the caller's own tickets (requester or subscriber) across organizations. */
export const myTicketListSchema = z.object({ items: z.array(ticketListItemSchema) });
/**
 * The hub list's filters. status is a comma-separated list of customer statuses; assignee is 'me', 'unassigned' or a
 * user id.
 */
export const ticketListQuerySchema = z.object({
    status: z
        .string()
        .max(200)
        .refine((v) => v.split(',').every((s) => TICKET_CUSTOMER_STATUSES.includes(s)), 'a comma-separated list of customer statuses')
        .optional(),
    assignee: z.union([z.enum(['me', 'unassigned']), z.uuid()]).optional(),
    product: z.string().min(1).max(100).optional(),
    /**
     * 0.18.0 (additive): search, as the staff queue's: a ticket number (exact or its start: NWF-14) or words of the
     * title (case-insensitive; % and _ are matched as typed, never as wildcards).
     */
    q: z.string().trim().min(1).max(200).optional(),
    cursor: z.string().max(500).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
/**
 * The answer to a new message. A reply by the person who filed a ticket resolved more than 14 days ago starts a new
 * linked ticket instead: the message is then that ticket's first one and createdTicketNumber names it.
 */
export const ticketMessageResultSchema = ticketMessageSchema.extend({
    createdTicketNumber: z.string().optional(),
});
/**
 * The body of the hub's actions (assign, escalate, resolve, reopen, take-back). assign needs assigneeUserId (null
 * unassigns); escalate needs a note for Trace; take-back (0.18.0, additive: the help desk takes an escalated ticket
 * back from Trace) needs a note too. Checked per action by the service, after the caller's permission.
 */
export const ticketActionRequestSchema = z.object({
    assigneeUserId: z.uuid().nullable().optional(),
    note: z.string().trim().min(1).max(2000).optional(),
});
/** A ticket number: the organization's code (or T for Trace's global sequence) and its sequence (NWF-142). */
export const TICKET_NUMBER_PATTERN = /^[A-Z]{1,5}-[1-9][0-9]{0,11}$/;
export const ticketNumberSchema = z.string().regex(TICKET_NUMBER_PATTERN, 'a ticket number such as NWF-142');
/** An organization's ticket code: 2 to 5 capital letters (NWF). */
export const TICKET_CODE_PATTERN = /^[A-Z]{2,5}$/;
/** Comma-separated values of one vocabulary, as a query string carries a filter list. */
const commaList = (values, what) => z
    .string()
    .max(500)
    .refine((v) => v.split(',').every((s) => values.includes(s)), `a comma-separated list of ${what}`);
/** One row of Trace's staff queue: the list row plus Trace's own fields and the response target. */
export const ticketStaffListItemSchema = ticketListItemSchema.extend({
    organizationName: z.string(),
    traceStatus: ticketTraceStatusSchema.nullable(),
    priority: ticketPrioritySchema,
    labels: z.array(ticketLabelSchema),
    traceAssignee: ticketPersonSchema.nullable(),
    escalatedAt: z.iso.datetime().nullable(),
    /** When the first Trace reply is due (null: the priority has no target). */
    responseDueAt: z.iso.datetime().nullable(),
    firstTraceReplyAt: z.iso.datetime().nullable(),
    /** No Trace reply yet and the response target has passed. */
    overdue: z.boolean(),
});
export const TICKET_STAFF_VIEWS = ['inbox', 'mine', 'all'];
/**
 * The staff queue's filters. view: inbox (escalations still in Triage, and tickets with a customer reply newer than
 * the caller last read them), mine (assigned to the caller) or all. status and priority are comma-separated Trace
 * statuses and priorities, labelIds comma-separated label ids (a ticket with any of them), assignee 'none' or a staff
 * user id, overdue 'true' for tickets past their response target without a Trace reply.
 */
export const ticketStaffQueueQuerySchema = z.object({
    view: z.enum(TICKET_STAFF_VIEWS).default('all'),
    status: commaList(TICKET_TRACE_STATUSES, 'Trace statuses').optional(),
    priority: commaList(TICKET_PRIORITIES, 'priorities').optional(),
    labelIds: z
        .string()
        .max(2000)
        .refine((v) => v.split(',').every((s) => z.uuid().safeParse(s).success), 'a comma-separated list of label ids')
        .optional(),
    assignee: z.union([z.literal('none'), z.uuid()]).optional(),
    orgId: z.uuid().optional(),
    product: z.string().min(1).max(100).optional(),
    overdue: z.enum(['true', 'false']).optional(),
    /**
     * 0.18.0 (additive): search. A ticket number (exact or its start: NWF-14) or words of the title (case-insensitive;
     * % and _ are matched as typed, never as wildcards).
     */
    q: z.string().trim().min(1).max(200).optional(),
    /** 0.18.0 (additive): only tickets filed at least this many days ago (the age filter). */
    olderThanDays: z.coerce.number().int().min(1).max(3650).optional(),
    cursor: z.string().max(500).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
export const ticketStaffQueueSchema = z.object({
    items: z.array(ticketStaffListItemSchema),
    next: z.string().nullable(),
    /** The caller's Inbox and My tickets counts (whatever the filters), for the navigation badges. */
    counts: z.object({ inbox: z.number().int().nonnegative(), mine: z.number().int().nonnegative() }),
});
/**
 * A staff change of a ticket (PATCH /v1/admin/tickets/{number}). subscribe (follow the ticket or stop) is open to
 * every staff role that reads tickets; the other fields need staff.tickets.manage. labelIds replaces the ticket's
 * labels; staffAssigneeUserId null unassigns.
 */
export const ticketStaffUpdateRequestSchema = z
    .object({
    traceStatus: ticketTraceStatusSchema.optional(),
    priority: ticketPrioritySchema.optional(),
    staffAssigneeUserId: z.uuid().nullable().optional(),
    labelIds: z.array(z.uuid()).max(20).optional(),
    subscribe: z.boolean().optional(),
})
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'at least one field to change');
/**
 * GET /v1/admin/ticket-assignees (staff.tickets.manage): the active staff a ticket can be assigned to (roles that work
 * tickets), names only, for the assignee menu.
 */
export const ticketAssigneeListSchema = z.object({ items: z.array(ticketSubscriberSchema) });
/** Trace hands the ticket back to the customer's help desk, with a note the customer reads. */
export const ticketHandBackRequestSchema = z.object({ note: z.string().trim().min(1).max(2000) });
/**
 * A link on a ticket: a Linear issue (an https://linear.app/ URL and its key, shown as a chip, no sync), or another
 * ticket (related, or duplicate_of, which closes this ticket as Duplicate and points to the original).
 */
export const ticketLinkCreateRequestSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('linear'),
        url: z
            .url({ protocol: /^https$/ })
            .max(500)
            .refine((u) => new URL(u).hostname === 'linear.app', 'a https://linear.app/ issue URL'),
        key: z.string().regex(/^[A-Z][A-Z0-9]{0,9}-[1-9][0-9]{0,9}$/, 'an issue key such as TE-812'),
    }),
    z.object({ kind: z.enum(['related', 'duplicate_of']), targetNumber: ticketNumberSchema }),
]);
/**
 * Trace staff file a ticket for a customer (after a phone call, say): it starts in Todo, already with Trace, and the
 * organization's help desk sees it. The requester is an active member of the organization and receives the replies.
 */
export const staffTicketCreateRequestSchema = z.object({
    organizationId: z.uuid(),
    requesterUserId: z.uuid(),
    title: z.string().trim().min(3).max(140),
    body: bodySchema,
    urgency: ticketUrgencySchema.optional(),
    priority: ticketPrioritySchema.optional(),
    product: z.string().min(1).max(100).optional(),
    attachmentIds: attachmentIdsSchema,
});
/** A label as Trace Admin manages it: archived labels stay on the tickets that carry them. */
export const ticketLabelAdminSchema = ticketLabelSchema.extend({
    color: z.string(),
    archivedAt: z.iso.datetime().nullable(),
});
export const ticketLabelListSchema = z.object({ items: z.array(ticketLabelAdminSchema) });
const labelColorSchema = z.string().regex(/^#[0-9a-f]{6}$/, 'a lowercase hex colour such as #3b82f6');
const labelNameSchema = z.string().trim().min(1).max(40);
const labelGroupSchema = z.string().trim().min(1).max(40);
export const ticketLabelCreateRequestSchema = z.object({
    name: labelNameSchema,
    color: labelColorSchema,
    group: labelGroupSchema.nullable().optional(),
});
/** Rename, recolour, regroup (null: no group), archive (true) or restore (false) a label. */
export const ticketLabelUpdateRequestSchema = z
    .object({
    name: labelNameSchema.optional(),
    color: labelColorSchema.optional(),
    group: labelGroupSchema.nullable().optional(),
    archived: z.boolean().optional(),
})
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'at least one field to change');
/** A first-reply target: hours, or business days (Monday to Friday, UTC). */
export const ticketResponseTargetSchema = z.union([
    z.object({ hours: z.number().int().min(1).max(720) }).strict(),
    z.object({ businessDays: z.number().int().min(1).max(30) }).strict(),
]);
export const ticketResponseTargetsSchema = z
    .object({
    urgent: ticketResponseTargetSchema.optional(),
    high: ticketResponseTargetSchema.optional(),
    medium: ticketResponseTargetSchema.optional(),
    low: ticketResponseTargetSchema.optional(),
})
    .strict();
/** The response targets per priority (a priority without one has no target). */
export const ticketSettingsSchema = z.object({
    responseTargets: ticketResponseTargetsSchema,
    updatedAt: z.iso.datetime(),
});
export const ticketSettingsUpdateRequestSchema = z.object({ responseTargets: ticketResponseTargetsSchema });
/**
 * An organization's ticket code (null: its tickets use Trace's global T- sequence). Unique across organizations
 * (409 code_taken), and fixed once a ticket was numbered with it (409 code_in_use).
 */
export const orgTicketCodeRequestSchema = z.object({
    code: z.string().regex(TICKET_CODE_PATTERN, '2 to 5 capital letters').nullable(),
});
export const orgTicketCodeSchema = z.object({ organizationId: z.uuid(), ticketCode: z.string().nullable() });
/**
 * 0.18.0 (additive): the organization's "New tickets go to" setting (organizations.ticket_routing). The help desk
 * (tickets.read_all) reads it; owners and administrators (org.update) change it, audited.
 */
export const orgTicketRoutingSchema = z.object({ organizationId: z.uuid(), routing: ticketRoutingSchema });
export const orgTicketRoutingRequestSchema = z.object({ routing: ticketRoutingSchema });
/**
 * 0.18.0 (additive): the organization's active members as the help desk needs them (tickets.manage), names only: who
 * a ticket can be filed on behalf of, and (helpDesk: their roles work tickets) who it can be assigned to. No emails,
 * roles or statuses: the help desk role reads no member list.
 */
export const ticketPersonOptionSchema = z.object({
    id: z.uuid(),
    displayName: z.string(),
    helpDesk: z.boolean(),
});
export const ticketPeopleSchema = z.object({ items: z.array(ticketPersonOptionSchema) });
/**
 * Who a product acts for on the internal ticket routes: the person (an active member of the organization, 403
 * membership_inactive otherwise) and the product, which must be the calling service's own (403 product_mismatch).
 */
const internalTicketActorShape = {
    organizationId: z.uuid(),
    userId: z.uuid(),
    product: z.string().min(1).max(100),
};
export const internalTicketActorQuerySchema = z.object(internalTicketActorShape);
/** A product files a ticket for its user; the ticket's product is the caller's own. */
export const internalTicketCreateRequestSchema = z.object({
    ...internalTicketActorShape,
    ticket: ticketCreateRequestSchema.omit({ product: true, onBehalfOfUserId: true, routeToTrace: true }),
});
/** A product adds its user's reply to a ticket the user may see. */
export const internalTicketReplyRequestSchema = z.object({
    ...internalTicketActorShape,
    message: ticketReplyRequestSchema,
});
/**
 * Asking for an upload URL. sizeBytes is the exact size of the file (the upload URL is signed for it, and finalize
 * checks it); over TICKET_ATTACHMENT_MAX_BYTES answers 400 attachment_too_large. fileName is shown to readers and
 * used as the download's file name.
 */
export const ticketAttachmentPresignRequestSchema = z.object({
    fileName: z.string().trim().min(1).max(255),
    mime: z.enum(TICKET_ATTACHMENT_MIME_TYPES),
    sizeBytes: z.number().int().positive(),
    kind: z.enum(TICKET_ATTACHMENT_KINDS).default('file'),
});
/**
 * Where to upload: PUT the file's bytes to url with exactly these headers, before the URL expires (minutes), then call
 * finalize. The upload is pending until then, and an attachment not sent on a message within 24 hours is deleted.
 */
export const ticketAttachmentPresignSchema = z.object({
    id: z.uuid(),
    upload: z.object({
        url: z.url(),
        method: z.literal('PUT'),
        headers: z.record(z.string(), z.string()),
        expiresAt: z.iso.datetime(),
    }),
});
/** A finalized upload: its bytes are the declared type and size, and it can go on a message (attachmentIds). */
export const ticketAttachmentFinalizeSchema = z.object({ id: z.uuid(), status: z.literal('ready') });
/** A short-lived download URL (a few minutes): images open inline, PDFs download, CSVs download as text (<name>.csv.txt). */
export const ticketAttachmentDownloadSchema = z.object({ url: z.url(), expiresAt: z.iso.datetime() });
/** Staff upload for a ticket of an organization (a reply, a Trace note, or a ticket filed for a customer). */
export const staffTicketAttachmentPresignRequestSchema = ticketAttachmentPresignRequestSchema.extend({
    organizationId: z.uuid(),
});
/** A product uploads for its user, who then owns the upload (only they finalize it and send it on a message). */
export const internalTicketAttachmentPresignRequestSchema = z.object({
    ...internalTicketActorShape,
    attachment: ticketAttachmentPresignRequestSchema,
});
const enc = encodeURIComponent;
/** The hub's routes: an organization's own tickets, addressed by ticket number. */
export const TICKETS_API = {
    list: (orgId) => `/v1/orgs/${enc(orgId)}/tickets`,
    ticket: (orgId, n) => `/v1/orgs/${enc(orgId)}/tickets/${enc(n)}`,
    reply: (orgId, n) => `${TICKETS_API.ticket(orgId, n)}/messages`,
    action: (orgId, n, a) => `${TICKETS_API.ticket(orgId, n)}/${a}`,
    presign: (orgId) => `/v1/orgs/${enc(orgId)}/ticket-attachments`,
    finalize: (orgId, id) => `/v1/orgs/${enc(orgId)}/ticket-attachments/${enc(id)}/finalize`,
    download: (orgId, id) => `/v1/orgs/${enc(orgId)}/ticket-attachments/${enc(id)}`,
    mine: '/v1/me/tickets',
    /** GET (tickets.read_all) and PUT (org.update): the "New tickets go to" setting. */
    routing: (orgId) => `/v1/orgs/${enc(orgId)}/ticket-routing`,
    /** GET (tickets.manage): the active members, for On behalf of and the assignee choice. */
    people: (orgId) => `/v1/orgs/${enc(orgId)}/ticket-people`,
};
/** Trace Admin's routes (staff, admin audience). */
export const ADMIN_TICKETS_API = {
    queue: '/v1/admin/tickets',
    /** POST: staff file a ticket for a customer (the queue's path). */
    create: '/v1/admin/tickets',
    ticket: (n) => `/v1/admin/tickets/${enc(n)}`,
    reply: (n) => `${ADMIN_TICKETS_API.ticket(n)}/messages`,
    update: (n) => ADMIN_TICKETS_API.ticket(n),
    handBack: (n) => `${ADMIN_TICKETS_API.ticket(n)}/hand-back`,
    links: (n) => `${ADMIN_TICKETS_API.ticket(n)}/links`,
    link: (n, linkId) => `${ADMIN_TICKETS_API.ticket(n)}/links/${enc(linkId)}`,
    labels: '/v1/admin/ticket-labels',
    label: (id) => `/v1/admin/ticket-labels/${enc(id)}`,
    settings: '/v1/admin/ticket-settings',
    /** GET: who a ticket can be assigned to (staff.tickets.manage). */
    assignees: '/v1/admin/ticket-assignees',
    orgTicketCode: (orgId) => `/v1/admin/organizations/${enc(orgId)}/ticket-code`,
    /** POST, with the organization in the body (staffTicketAttachmentPresignRequestSchema). */
    presign: '/v1/admin/ticket-attachments',
    finalize: (id) => `/v1/admin/ticket-attachments/${enc(id)}/finalize`,
    download: (id) => `/v1/admin/ticket-attachments/${enc(id)}`,
};
/** Products' routes (service tokens, internal audience): a product files and reads tickets for its users. */
export const INTERNAL_TICKETS_API = {
    create: '/v1/internal/tickets',
    listMine: '/v1/internal/tickets/mine',
    ticket: (n) => `/v1/internal/tickets/${enc(n)}`,
    reply: (n) => `/v1/internal/tickets/${enc(n)}/messages`,
    presign: '/v1/internal/tickets/attachments',
    /** POST with the actor (organizationId, userId, product) as the body. */
    finalize: (id) => `/v1/internal/tickets/attachments/${enc(id)}/finalize`,
    /** GET with the actor as the query string. */
    download: (id) => `/v1/internal/tickets/attachments/${enc(id)}`,
};
//# sourceMappingURL=tickets.js.map